import { useEffect, useState } from "react";
import type { ConnectorDTO, SubjectTypeDTO } from "../../../shared/types";
import { Icon } from "../../components/Icon";
import { Dialog } from "../../components/Dialog";
import { ToolChip, TypeForm, typeReady, type TypeDraft } from "../../components/ScopeTypeForm";
import { ShowArchived, useShowArchived } from "../../components/ShowArchived";
import { Badge, Button, ErrorText, PageHeader } from "../../components/ui";
import { get, post } from "../../lib/api";
import { typePath, useScopeTypes } from "../../lib/scopes";
import { Link, navigate } from "../../lib/router";

function NewTypeForm({ connectors, onCreated }: { connectors: ConnectorDTO[]; onCreated: (type: SubjectTypeDTO) => void }) {
  const [draft, setDraft] = useState<TypeDraft>({ key: "", name: "", pluralName: "", description: "", fields: [] });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-4">
      <TypeForm draft={draft} connectors={connectors} isNew onChange={setDraft} />
      <ErrorText error={error} />
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={busy || !typeReady(draft)}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const fields = draft.fields.map((f) => ({ system: f.system, idField: f.idField.trim() }));
              onCreated(await post<SubjectTypeDTO>("/api/subject-types", { key: draft.key, name: draft.name.trim(), pluralName: draft.pluralName.trim(), description: draft.description.trim(), fields }));
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Creating…" : "Create type"}
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-0">
      <div className="text-heading font-semibold tabular-nums">{value}</div>
      <div className="truncate text-xs text-muted">{label}</div>
    </div>
  );
}

/**
 * Build → Scopes: the types of scope (account, deal). A scope is <type>:<id>, one row shared by every agent working
 * under it; a type says which tools its scopes have an id in and which agents may work under them.
 */
export function AdminScopes() {
  // Every type, archived ones too, so the switch knows how many it hides.
  const { types: all, error, reload } = useScopeTypes({ archived: true });
  const [showArchived, setShowArchived] = useShowArchived();
  const archivedCount = all?.filter((t) => t.archivedAt).length ?? 0;
  const types = all && all.filter((t) => showArchived || !t.archivedAt);
  const [connectors, setConnectors] = useState<ConnectorDTO[]>([]);
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    get<ConnectorDTO[]>("/api/connectors").then(setConnectors, () => {});
  }, []);

  return (
    <div className="pb-10">
      <PageHeader
        title="Scopes"
        subtitle="A scope is one account, deal or other thing agents work on. It keeps its id in each of your tools, and memory, skills and files that every agent working under it shares. Every scope has a type, and agents are given access per type."
        actions={<Button variant="primary" onClick={() => setCreating(true)}>New type</Button>}
      />
      <div className="px-4 md:px-6">
        <ErrorText error={error} />
        {archivedCount > 0 && (
          <div className="mb-3 flex justify-end">
            <ShowArchived on={showArchived} onChange={setShowArchived} count={archivedCount} />
          </div>
        )}
        {types === null ? (
          !error && <div className="card h-40 animate-pulse" />
        ) : types.length === 0 ? (
          <div className="card flex flex-col items-center gap-3 px-6 py-12 text-center">
            <span className="grid size-12 place-items-center rounded-2xl bg-haze-soft text-slate dark:bg-haze/15 dark:text-haze">
              <Icon name="scopes" className="size-6" />
            </span>
            <p className="max-w-md text-sm text-muted">
              No scope types yet. A type, such as Account or Deal, says which tools its scopes have an id in and which agents may work under them.
            </p>
            <Button variant="primary" onClick={() => setCreating(true)}>Create the first type</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {types.map((t) => (
              <Link key={t.key} to={typePath(t.key)} className="card flex min-w-0 flex-col p-5 transition hover:shadow-md">
                <div className="flex items-start gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-haze-soft text-slate dark:bg-haze/15 dark:text-haze">
                    <Icon name="scopes" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-heading font-semibold tracking-tight">{t.name}</span>
                      {t.archivedAt && <Badge tone="amber">Archived</Badge>}
                    </div>
                    <div className="truncate font-mono text-xs text-muted">{t.key}:&lt;id&gt;</div>
                  </div>
                </div>
                <p className="mt-3 line-clamp-2 min-h-10 text-sm text-muted">{t.description || "No description"}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {t.fields.map((f, i) => (
                    <ToolChip key={f.system} field={f} connectors={connectors} main={i === 0} />
                  ))}
                  {t.fields.length === 0 && <span className="text-xs text-faint">No tools yet</span>}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 border-t border-line pt-3">
                  <Stat label={`${t.scopeCount === 1 ? "scope" : "scopes"}${t.archivedCount ? ` · ${t.archivedCount} archived` : ""}`} value={t.scopeCount} />
                  <Stat label={t.agentIds.length === 1 ? "agent with access" : "agents with access"} value={t.agentIds.length} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Dialog open={creating} onClose={() => setCreating(false)} size="lg" title="New scope type">
        <NewTypeForm
          connectors={connectors}
          onCreated={(type) => {
            setCreating(false);
            void reload();
            navigate(typePath(type.key));
          }}
        />
      </Dialog>
    </div>
  );
}
